export const SLR_LOAN_STANDING_FEATURE_FLAG = 'slrLoanStandingVerification';

export type SlrLoanStandingStatus =
  | 'unknown'
  | 'pending_upload'
  | 'processing'
  | 'eligible'
  | 'ineligible'
  | 'try_again'
  | 'cannot_determine';

export type SlrLoanStandingAction =
  'upload_document' | 'upload_newer_document' | 'confirm_name_match' | 'contact_support' | 'none';

/**
 * Raw good-standing state emitted by tio-api's EmployeePlanAccount
 * (`good_standing` column). The richer presentation status above
 * (`SlrLoanStandingStatus`) is derived from this plus the reason via
 * `deriveSlrLoanStandingStatus()`.
 */
export type SlrGoodStandingState = 'unknown' | 'eligible' | 'ineligible' | 'undeterminable';

/**
 * Raw good-standing reason emitted by tio-api's EmployeePlanAccount
 * (`good_standing_reason` column), plus the document-pipeline second-document
 * asks that surface through the same field (see tio-front-end#8714 / D4).
 */
export type SlrGoodStandingReason =
  | 'none'
  | 'missing_document'
  | 'stale_document'
  | 'outside_audit_window'
  | 'missing_required_observations'
  | 'name_mismatch'
  | 'name_match_confirmation_required'
  | 'status_not_in_good_standing'
  | 'delinquent_or_collections'
  | 'defaulted'
  | 'parent_plus_only'
  | 'undeterminable'
  | 'manual_override'
  | 'nslds_required_for_federal_non_repayment_status'
  | 'statement_required_for_repayment_status'
  // Cable-only (Slr::VerificationOutcomeBroadcaster, #9972): the uploaded
  // document's reflected observations all belong to a different account than
  // the EPA it was submitted against. Never persisted on the EPA.
  | 'document_for_different_account';

export interface SlrLoanStandingFailure {
  code: string;
  field?: string;
  message?: string;
}

export interface SlrLoanStandingAuditState {
  employeePlanAccountId?: string | null;
  /**
   * Name-review context for the fuzzy match (Figma v3 "Review Name" panel):
   * the EF-sourced employee name and the borrower name extracted from the
   * uploaded document (the account observation the eval compared against).
   */
  employerProvidedName?: string | null;
  documentName?: string | null;
  status: SlrLoanStandingStatus;
  primaryReason?: string | null;
  reasons?: string[];
  failures?: SlrLoanStandingFailure[];
  action: SlrLoanStandingAction;
  sourceDocumentId?: string | null;
  /** Filename of the document behind the verdict — the name-match card's red callout (#10128). */
  sourceDocumentFilename?: string | null;
  featureEnabled: boolean;

  /**
   * Audit-window context (RFC 012 audit months / contribution periods).
   * All three stay absent until tio-api exposes audit windows on the
   * verification payload — every consumer renders its default (non-audit)
   * presentation while unset, so shipping the UI ahead of the backend is safe.
   */
  /** Upload deadline for the current audit window (ISO date) — drives the "required by" chip. */
  auditRequiredBy?: string | null;
  /** The audit deadline passed without a qualifying document — drives the "past due!" chip. */
  auditPastDue?: boolean;
  /** End of the contribution period the current audit covers (ISO date) — drives period-aware copy. */
  contributionPeriodEnd?: string | null;
}

/**
 * Raw verdict broadcast by `Slr::VerificationOutcomeBroadcaster` (tio-api) on
 * the uploaded asset's ServiceResponseChannel stream after the direct-reference
 * evaluation settles. Deliberately raw: the server owns the verdict, the
 * client owns presentation — derive status/action via
 * `deriveSlrLoanStandingStatus`/`deriveSlrLoanStandingAction`.
 */
export interface SlrLoanStandingCablePayload {
  employeePlanAccountId?: string | null;
  assetUuid?: string | null;
  sourceDocumentId?: string | null;
  goodStanding?: SlrGoodStandingState | string;
  goodStandingReason?: SlrGoodStandingReason | string | null;
  goodStandingReasons?: string[];
  goodStandingDeterminedAt?: string | null;
}

export interface SlrLoanStandingPresentation {
  titleKey: string;
  descriptionKey: string;
  ctaKey?: string;
  intent: 'info' | 'warning' | 'success';
  requiresAttention: boolean;
}
